import {
  BTLOfferDetails,
  MobileBTLOffer,
  NormalizedBTLOffer,
  StaticPlan,
  DynamicPlan,
  StaticVasItem,
  DynamicVasItem
} from '../types/btloffer.types';
import { formatBTLPrice, getSelectedAddonBoidList } from './common';

const isStaticVas = (
  vas: StaticVasItem | DynamicVasItem
): vas is StaticVasItem => {
  return 'BOID' in vas && vas.BOID !== undefined;
};

export const isDynamicBoidOffer = (offerData: BTLOfferDetails): boolean => {
  const firstOffer = offerData?.mobile?.[0];
  if (!firstOffer) return false;
  return !!firstOffer.planDiscTechBO;
};

const normalizeStaticOffer = (
  firstOffer: MobileBTLOffer
): NormalizedBTLOffer => {
  const plan = firstOffer?.plan as StaticPlan;

  const vasBoidList = (firstOffer.vas ?? []).flatMap(vas => {
    if (isStaticVas(vas)) {
      return [vas.BOID, vas.discountBOID].filter(Boolean) as string[];
    }
    return [];
  });

  return {
    planBoid: plan.BOID?.toString() ?? '',
    planDiscountBoid: plan.discountBOID?.toString() ?? null,
    planTechBoid: null,
    contractDuration: plan.contractDuration ?? '',
    offerPriceDuration: plan.offerPriceDuration ?? '',
    vasBoidList: [...new Set(vasBoidList)],
    vasTechBOIDList: [],
    planTechBOIDOffer: null,
    pxVasList: [],
    devices: null,
    accessories: [],
    rebate: [],
    discountStockCode: [],
    displayItems: firstOffer.displayItems,
    price: firstOffer.price,
    rawVas: firstOffer.vas ?? []
  };
};

const normalizeDynamicOffer = (
  firstOffer: MobileBTLOffer
): NormalizedBTLOffer => {
  const plan = firstOffer?.plan as DynamicPlan;

  const vasBoidList = (firstOffer?.vas ?? []).flatMap(vas => {
    if (!isStaticVas(vas)) {
      return vas?.boId ? [vas?.boId] : [];
    }
    return [];
  });

  const vasTechBOIDOffers = firstOffer?.vasDiscTechBO ?? [];
  const planTechBOIDOffers = firstOffer?.planDiscTechBO ?? null;

  return {
    planBoid: plan.boId ?? '',
    planDiscountBoid: null,
    planTechBoid: firstOffer.planDiscTechBO?.techBoId ?? null,
    contractDuration: plan?.contractDuration ?? '',
    offerPriceDuration: plan?.offerPriceDuration ?? '',
    vasBoidList: [...new Set(vasBoidList)],
    vasTechBOIDList: [...new Set(vasTechBOIDOffers)],
    planTechBOIDOffer: planTechBOIDOffers,
    pxVasList: firstOffer.pxVas ?? [],
    devices: firstOffer.devices ?? null,
    accessories: firstOffer.accessories ?? [],
    rebate: Array.isArray(firstOffer.rebate) ? firstOffer.rebate : [],
    discountStockCode: firstOffer.discountStockCode ?? [],
    displayItems: firstOffer.displayItems,
    price: firstOffer.price,
    rawVas: firstOffer.vas ?? []
  };
};

export const normalizeBTLOffer = (
  offerData: BTLOfferDetails
): NormalizedBTLOffer | null => {
  const firstOffer = offerData?.mobile?.[0];
  if (!firstOffer) return null;

  return isDynamicBoidOffer(offerData)
    ? normalizeDynamicOffer(firstOffer)
    : normalizeStaticOffer(firstOffer);
};

export const buildVasBoidArray = (addons, normalizedOffer) => {
  const vasBoids = [
    ...(addons?.btlAddon?.btlNonPxAddons || []),
    normalizedOffer?.planDiscountBoid,
    ...(getSelectedAddonBoidList(addons?.btlAddon?.addonData) || [])
  ].filter(Boolean) as string[];

  return [...new Set(vasBoids)];
};

export const buildDynamicTechBoidOfferData = normalizedOffer => ({
  vasTechBoidOffers: [...(normalizedOffer?.vasTechBOIDList || [])],
  rebateOffers: [...(normalizedOffer?.rebate || [])],
  planTechBoidOffer: normalizedOffer?.planTechBOIDOffer
});

export const buildBTLOrderPayload = ({
  selectedPlan,
  service,
  user,
  product,
  addons,
  promotions,
  normalizedOffer,
  addonList,
  allAddonPreTickList
}) => {
  const { selectedProduct } = product;

  return {
    selectedPlan: selectedPlan,
    service: service,
    contact: user.information.clientContext.contact,
    cisDetails: user.cis.information,
    simpleProductSpecId: selectedProduct?.productId,
    monthlyTerm: selectedProduct?.monthlyTerm,
    preTickAddons: allAddonPreTickList,
    selectedProduct: selectedProduct?.groupId,
    btlVasIds: buildVasBoidArray(addons, normalizedOffer),
    campaignId: promotions?.btlCampignId,
    addonList,
    dynamicTechBoidOfferData: buildDynamicTechBoidOfferData(normalizedOffer)
  };
};

export const getDurationLiner = mobileData => {
  const rawDuration = mobileData?.plan?.offerPriceDuration || '';
  let durationLiner = '';

  const offerPriceDuration = rawDuration ? rawDuration.match(/\d+/)?.[0] : null;
  const normalPrice = Number(mobileData?.price?.normalPrice) || 0;
  const offerPrice = Number(mobileData?.price?.offerPrice) || 0;

  if (
    offerPrice < normalPrice &&
    offerPriceDuration &&
    Number(offerPriceDuration) > 0
  ) {
    durationLiner = `For first ${Number(
      offerPriceDuration
    )} months, ${formatBTLPrice({
      price: normalPrice,
      suffix: '/mth'
    })} thereafter`;
  }

  return durationLiner;
};

export const getStickyFooterLiner = normalizedOffer => {
  if (!normalizedOffer) return [];

  const { price, contractDuration, offerPriceDuration } = normalizedOffer;
  const { offerPrice, normalPrice } = price ?? {};

  const formatPrice = p =>
    formatBTLPrice({
      price: p,
      suffix: '/mth'
    });

  if (
    offerPriceDuration &&
    Number(offerPriceDuration.match(/\d+/)?.[0]) > 0 &&
    offerPrice < normalPrice
  )
    return [
      {
        label: `For first ${offerPriceDuration}`,
        text: formatPrice(offerPrice)
      }
    ];

  if (isUnContractedOffer(normalizedOffer))
    return [{ label: 'No contract', text: formatPrice(offerPrice) }];

  if (contractDuration && Number(contractDuration) > 0)
    return [
      {
        label: `For ${contractDuration}`,
        text: formatPrice(offerPrice)
      }
    ];

  return [];
};

export const isUnContractedOffer = offer => {
  return offer?.contractDuration === 'Uncontracted';
};